import MuxPlayerClient from "@components/MuxPlayerClient"
import breakpoints from "@constants/breakpoints"
import colors from "@constants/colors"
import React from "react"
import MediaQuery from "react-responsive"
import styled from "styled-components"

const VideoWrapper = styled.div<{ $hideControls?: boolean }>`
  width: 100%;
  height: 100%;

  > * {
    height: 100%;
    --controls: ${({ $hideControls }) => $hideControls && "none"};
    --media-object-fit: cover;
    --media-object-position: center;
    object-fit: cover;
    object-position: center;
  }
`

export interface ContentfulMuxVideo {
  mobileVideo?: {
    assetId: string
    audioOnly: boolean
    created_at: number
    duration: number
    max_stored_frame_rate: number
    max_stored_resolution: string
    playbackId: string
    ratio: string
    ready: boolean
    version: number
  }
  thumbnailTime?: string
  title: string
  video: {
    assetId: string
    audioOnly: boolean
    created_at: number
    duration: number
    max_stored_frame_rate: number
    max_stored_resolution: string
    playbackId: string
    ratio: string
    ready: boolean
    version: number
  }
}

interface MuxVideoProps {
  autoplayVideo?: boolean
  hideControls?: boolean
  loopVideo?: boolean
  muxMobileVideo?: ContentfulMuxVideo | null | undefined // legacy
  muxVideo?: ContentfulMuxVideo | null | undefined // mobileVideo?
  onEnded?: () => void
  onPlaying?: (isMobile: boolean) => void
}

type ResolvedMux = {
  playbackId?: string
  thumbnailTime?: number
  title?: string
}

export default function MuxVideo({
  autoplayVideo = true,
  hideControls = false,
  loopVideo = true,
  muxMobileVideo,
  muxVideo,
  onEnded,
  onPlaying,
}: MuxVideoProps) {
  if (typeof window === "undefined") return null

  return (
    <MediaQuery query={breakpoints.mobileAndTabletOnly}>
      {(isMobile) => {
        const { playbackId, thumbnailTime, title } = resolveMux(
          isMobile,
          muxVideo,
          muxMobileVideo
        )

        if (!playbackId) return null

        const videoProps: any = {
          accentColor: colors.brandOrange,
          autoPlay: autoplayVideo ? "muted" : false,
          loop: loopVideo,
          nohotkeys: hideControls,
          onEnded,
          onPlaying: onPlaying ? () => onPlaying(isMobile) : undefined,
          playbackId,
          streamType: "on-demand",
          title: hideControls ? undefined : title,
          ...(typeof thumbnailTime === "number" ? { thumbnailTime } : {}),
        }

        return (
          <VideoWrapper $hideControls={hideControls}>
            <MuxPlayerClient {...videoProps} />
          </VideoWrapper>
        )
      }}
    </MediaQuery>
  )
}

function resolveMux(
  isMobile: boolean,
  muxVideo?: ContentfulMuxVideo | null,
  muxMobileVideo?: ContentfulMuxVideo | null
): ResolvedMux {
  const playbackId = isMobile
    ? (muxVideo?.mobileVideo?.playbackId ??
      muxMobileVideo?.video?.playbackId ??
      muxVideo?.video?.playbackId)
    : muxVideo?.video?.playbackId

  const mobileTitle = muxVideo?.mobileVideo
    ? muxVideo?.title
    : muxMobileVideo?.title
  const desktopTitle = muxVideo?.title
  const title = isMobile ? (mobileTitle ?? desktopTitle) : desktopTitle

  const mobileThumbnailTimeString = muxVideo?.mobileVideo
    ? muxVideo?.thumbnailTime
    : muxMobileVideo?.thumbnailTime
  const desktopThumbnailTimeString = muxVideo?.thumbnailTime
  const thumbString = isMobile
    ? (mobileThumbnailTimeString ?? desktopThumbnailTimeString)
    : desktopThumbnailTimeString
  const thumbnailTime = thumbString ? parseInt(thumbString, 10) : undefined

  return { playbackId, thumbnailTime, title }
}
